import { Box, Tooltip, Typography, makeStyles } from '@material-ui/core';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import FlashOnIcon from '@material-ui/icons/FlashOn';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { NodeCard } from './NodeCard';
import { JourneyNode, JourneyStep, Lane } from './types';

const LABEL_WIDTH = 150;
const STEP_WIDTH = 236;

const LANES: { id: Lane; label: string; hint: string }[] = [
  {
    id: 'edge',
    label: 'Step edge',
    hint: 'Only on some steps',
  },
  {
    id: 'ui',
    label: 'What the user sees',
    hint: 'The screen and the app behind it',
  },
  { id: 'services', label: 'Services', hint: 'The APIs the screen calls' },
  { id: 'adaptors', label: 'Adaptors', hint: 'Wrap the providers underneath' },
];

const useStyles = makeStyles(theme => ({
  scroller: { overflowX: 'auto', paddingBottom: theme.spacing(2) },
  grid: {
    display: 'grid',
    columnGap: theme.spacing(2),
    rowGap: theme.spacing(1),
  },
  label: {
    position: 'sticky',
    left: 0,
    zIndex: 1,
    background: theme.palette.background.default,
    paddingTop: theme.spacing(1),
  },
  cell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 8,
  },
  connector: { display: 'flex', justifyContent: 'center', color: '#9aa0ad' },
  header: {
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  number: {
    flex: 'none',
    width: 26,
    height: 26,
    borderRadius: '50%',
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 700,
    fontSize: 13,
    display: 'grid',
    placeItems: 'center',
  },
}));

const Connector = ({ isAsync }: { isAsync: boolean }) => {
  const classes = useStyles();
  return (
    <Box className={classes.connector}>
      {isAsync ? (
        <Tooltip title="Event-driven: not a direct call. See the step notes.">
          <FlashOnIcon fontSize="small" style={{ color: '#d97706' }} />
        </Tooltip>
      ) : (
        <ArrowDownwardIcon fontSize="small" />
      )}
    </Box>
  );
};

const StepHeader = ({ step }: { step: JourneyStep }) => {
  const classes = useStyles();
  return (
    <Box className={classes.header} style={{ opacity: step.isLive ? 1 : 0.55 }}>
      <Box className={classes.number}>{step.number}</Box>
      <Box flex={1} minWidth={0}>
        <Typography style={{ fontWeight: 600, lineHeight: 1.25 }}>
          {step.title}
        </Typography>
        {step.quote && (
          <Typography variant="caption" color="textSecondary">
            “{step.quote}”
          </Typography>
        )}
      </Box>
      {step.notes.length > 0 && (
        <Tooltip
          interactive
          placement="bottom"
          title={
            <Box p={0.5}>
              {step.notes.map(note => (
                <Typography key={note} variant="body2" paragraph>
                  {note}
                </Typography>
              ))}
            </Box>
          }
        >
          <InfoOutlinedIcon fontSize="small" color="action" />
        </Tooltip>
      )}
    </Box>
  );
};

type Props = {
  steps: JourneyStep[];
  /** The edge components every step passes through. */
  edge: JourneyNode[];
  activeId?: string;
  activeTeam?: string;
  onHover: (id?: string) => void;
};

type Dimmed = (id: string, team: string) => boolean;

const LaneLabel = ({ label, hint }: { label: string; hint: string }) => {
  const classes = useStyles();
  return (
    <Box className={classes.label}>
      <Typography style={{ fontWeight: 600, fontSize: 13 }}>{label}</Typography>
      <Typography variant="caption" color="textSecondary">
        {hint}
      </Typography>
    </Box>
  );
};

/** What every request passes through first, as one band across all the steps. */
const EdgeBand = ({
  edge,
  isDimmed,
  onHover,
}: {
  edge: JourneyNode[];
  isDimmed: Dimmed;
  onHover: (id?: string) => void;
}) => (
  <>
    <LaneLabel label="Every request" hint="Passes through the edge first" />
    <Box display="flex" style={{ gridColumn: '2 / -1', gap: 8 }}>
      {edge.map(node => (
        <Box key={node.id} flex={1}>
          <NodeCard
            node={node}
            isDimmed={isDimmed(node.id, node.team)}
            onHover={onHover}
          />
        </Box>
      ))}
    </Box>
  </>
);

const laneIds = LANES.map(lane => lane.id);

/** One layer across all the steps, with an arrow down to the next layer a step uses. */
const LaneRow = ({
  lane,
  index,
  steps,
  isDimmed,
  onHover,
}: {
  lane: (typeof LANES)[number];
  index: number;
  steps: JourneyStep[];
  isDimmed: Dimmed;
  onHover: (id?: string) => void;
}) => {
  const classes = useStyles();
  const hasLaterLane = (step: JourneyStep) =>
    laneIds.slice(index + 1).some(id => step.lanes[id].length > 0);
  return (
    <>
      <LaneLabel label={lane.label} hint={lane.hint} />
      {steps.map(step => (
        <Box key={step.id} className={classes.cell}>
          {step.lanes[lane.id].map(node => (
            <NodeCard
              key={node.id}
              node={node}
              isDimmed={isDimmed(node.id, node.team)}
              onHover={onHover}
            />
          ))}
          {step.lanes[lane.id].length > 0 && hasLaterLane(step) && (
            <Connector isAsync={step.isAsync && lane.id === 'services'} />
          )}
        </Box>
      ))}
    </>
  );
};

/** Fades everything except the hovered service and the chosen team. */
const dimmedBy =
  (activeId?: string, activeTeam?: string): Dimmed =>
  (id, team) =>
    Boolean(
      (activeId && id !== activeId) || (activeTeam && team !== activeTeam),
    );

/** Steps left to right, layers top to bottom, with arrows down the layers. */
export const Storyboard = ({
  steps,
  edge,
  activeId,
  activeTeam,
  onHover,
}: Props) => {
  const classes = useStyles();
  const isDimmed = dimmedBy(activeId, activeTeam);
  const lanes = LANES.filter(lane =>
    steps.some(step => step.lanes[lane.id].length > 0),
  );

  return (
    <Box className={classes.scroller}>
      <Box
        className={classes.grid}
        style={{
          gridTemplateColumns: `${LABEL_WIDTH}px repeat(${steps.length}, ${STEP_WIDTH}px)`,
        }}
      >
        <Box />
        {steps.map(step => (
          <StepHeader key={step.id} step={step} />
        ))}
        {edge.length > 0 && (
          <EdgeBand edge={edge} isDimmed={isDimmed} onHover={onHover} />
        )}
        {lanes.map(lane => (
          <LaneRow
            key={lane.id}
            lane={lane}
            index={laneIds.indexOf(lane.id)}
            steps={steps}
            isDimmed={isDimmed}
            onHover={onHover}
          />
        ))}
      </Box>
    </Box>
  );
};
